import { formatDate } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
	LoButton,
	LoCard,
	LoChip,
	LoNotice,
	LoPageHeader,
	LoSkeleton,
	LoStatus,
	LoToastService,
	LoTooltip
} from 'lo-design-system';
import { Subject, switchMap, takeUntil } from 'rxjs';
import { AnaAssistantFacadeService } from '@brand-lolly/app/_services/ana-assistant-facade.service';
import {
	BrandFactKind,
	BrandKnowledgeResponse,
	BrandKnowledgeRow,
	BrandKnowledgeService,
	KnowledgeFact,
	VoiceSummary
} from '@brand-lolly/app/user-management/new-sprint/services/brand-knowledge.service';

const DATE_FORMAT = 'd MMM';
const DATE_LOCALE = 'en-US';

interface FactView {
	kind: BrandFactKind;
	label: string;
	value: string;
	inheritedLabel: string | null;
	provenanceLabel: string | null;
	conversationId: string | null;
	canClear: boolean;
	clearKey: string;
}

interface BrandCardView {
	id: string;
	name: string;
	roleLabel: string;
	/** The brand the account registered with, as opposed to a product added later. */
	isRegistration: boolean;
	facts: FactView[];
}

interface VoiceView {
	summary: string | null;
	learnedLabel: string | null;
}

@Component({
	standalone: true,
	selector: 'app-assistant-knowledge-page',
	templateUrl: './assistant-knowledge-page.component.html',
	styleUrls: ['./assistant-knowledge-page.component.scss'],
	imports: [
		LoButton,
		LoCard,
		LoChip,
		LoNotice,
		LoPageHeader,
		LoSkeleton,
		LoStatus,
		LoTooltip
	]
})
export class AssistantKnowledgePageComponent implements OnInit, OnDestroy {
	// TRAP: starts TRUE — ngOnInit always fetches, and false would flash the empty rows first.
	loading = true;
	failed = false;
	brands: BrandCardView[] = [];
	voice: VoiceView | null = null;
	clearingKey: string | null = null;
	readonly skeletonCards = [1, 2];

	private unsubscriber = new Subject<void>();

	constructor(
		private knowledgeService: BrandKnowledgeService,
		private anaFacade: AnaAssistantFacadeService,
		private toasts: LoToastService
	) {}

	ngOnInit(): void {
		this.knowledgeService
			.getKnowledge()
			.pipe(takeUntil(this.unsubscriber))
			.subscribe({
				next: response => {
					this.applyResponse(response);
					this.loading = false;
				},
				error: () => {
					this.failed = true;
					this.loading = false;
				}
			});
	}

	clear(brand: BrandCardView, fact: FactView): void {
		if (this.clearingKey) {
			return;
		}
		this.clearingKey = fact.clearKey;
		this.knowledgeService
			.clear(brand.id, fact.kind)
			.pipe(
				// ORDER: redraw from the server's answer, never from local state —
				// clearing a product's own value can reveal an inherited one.
				switchMap(() => this.knowledgeService.getKnowledge()),
				takeUntil(this.unsubscriber)
			)
			.subscribe({
				next: response => {
					this.applyResponse(response);
					this.clearingKey = null;
				},
				error: () => {
					this.clearingKey = null;
					this.toasts.error(
						`We could not clear ${fact.label}. Try again.`
					);
				}
			});
	}

	openConversation(fact: FactView): void {
		if (fact.conversationId) {
			this.anaFacade.openConversation(fact.conversationId);
		}
	}

	ngOnDestroy(): void {
		this.unsubscriber.next();
		this.unsubscriber.complete();
	}

	private applyResponse(response: BrandKnowledgeResponse): void {
		this.failed = false;
		this.brands = response.brands.map(row => this.toBrandCard(row));
		this.voice = this.toVoice(response.voice);
	}

	private toBrandCard(row: BrandKnowledgeRow): BrandCardView {
		return {
			id: row.id,
			name: row.brand_name,
			isRegistration: row.source === 'registration',
			roleLabel:
				row.source === 'registration' ? 'Registered brand' : 'Product',
			facts: row.facts.map(fact => this.toFact(row, fact))
		};
	}

	private toFact(row: BrandKnowledgeRow, fact: KnowledgeFact): FactView {
		return {
			kind: fact.kind,
			label: fact.label,
			value: fact.value,
			inheritedLabel: fact.own
				? null
				: `Inherited from ${
						fact.inherited_from_name ?? 'the registered brand'
				  }`,
			provenanceLabel: this.provenanceLabel(fact),
			conversationId:
				fact.source === 'conversation' ? fact.conversation_id : null,
			canClear: fact.own,
			clearKey: `${row.id}:${fact.kind}`
		};
	}

	private provenanceLabel(fact: KnowledgeFact): string | null {
		if (fact.source === 'settings') {
			return 'Settings';
		}
		if (fact.source === 'conversation') {
			return fact.at
				? `Conversation · ${formatDate(
						fact.at,
						DATE_FORMAT,
						DATE_LOCALE
				  )}`
				: 'Conversation';
		}
		return null;
	}

	private toVoice(voice: VoiceSummary | null): VoiceView | null {
		if (!voice || voice.status !== 'ready') {
			return null;
		}
		return {
			summary: voice.summary,
			learnedLabel: voice.analyzed_at
				? `Learned ${formatDate(
						voice.analyzed_at,
						DATE_FORMAT,
						DATE_LOCALE
				  )}`
				: null
		};
	}
}
